import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { PlusCircle, Eye, TrendingUp, Power, AlertTriangle, ExternalLink, X, ShieldAlert } from 'lucide-react';
import { cn } from '../../infrastructure/lib/utils';
import {
  listTemplates,
  updateTemplateStatus,
  getTemplatePublishedSites,
  stopTemplatePublishedSites,
  type AdminTemplate,
  type AdminTemplatePublishedSite,
} from '../../infrastructure/lib/api';

export default function TemplatesPage() {
  const [items, setItems] = useState<AdminTemplate[]>([]);
  const [category, setCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Modal state for published site inspection / revocation
  const [selectedTemplate, setSelectedTemplate] = useState<AdminTemplate | null>(null);
  const [publishedSites, setPublishedSites] = useState<AdminTemplatePublishedSite[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [sitesError, setSitesError] = useState<string | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [stoppingSites, setStoppingSites] = useState(false);

  const fetchTemplates = async () => {
    try {
      const res = await listTemplates({ limit: 60 });
      setItems(res.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleToggleActive = async (template: AdminTemplate) => {
    setTogglingId(template.id);
    try {
      const updated = await updateTemplateStatus(template.id, !template.isActive);
      setItems((prev) =>
        prev.map((t) => (t.id === template.id ? { ...t, isActive: updated.isActive } : t)),
      );
    } catch (e) {
      alert(`Failed to update status: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleOpenSitesModal = async (template: AdminTemplate) => {
    setSelectedTemplate(template);
    setLoadingSites(true);
    setSitesError(null);
    try {
      const sites = await getTemplatePublishedSites(template.id);
      setPublishedSites(sites);
    } catch (e) {
      setSitesError(e instanceof Error ? e.message : 'Failed to load published sites');
    } finally {
      setLoadingSites(false);
    }
  };

  const handleStopPublishedSites = async () => {
    if (!selectedTemplate) return;
    const confirmStop = window.confirm(
      `Are you sure you want to STOP and suspend all ${publishedSites.length} live published site(s) using "${selectedTemplate.name}"? This will make their hosted URLs inaccessible to guests.`,
    );
    if (!confirmStop) return;

    setStoppingSites(true);
    try {
      const res = await stopTemplatePublishedSites(selectedTemplate.id, revokeReason || undefined);
      alert(`Successfully suspended ${res.stoppedCount} published site(s).`);
      setPublishedSites([]);
      fetchTemplates();
      setSelectedTemplate(null);
    } catch (e) {
      alert(`Failed to stop published sites: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setStoppingSites(false);
    }
  };

  const categories = useMemo(() => {
    const set = new Set<string>();
    items.forEach((t) => set.add(t.occasionType));
    return ['All Templates', ...Array.from(set).sort()];
  }, [items]);

  const filtered = useMemo(
    () =>
      category && category !== 'All Templates'
        ? items.filter((t) => t.occasionType === category)
        : items,
    [items, category],
  );

  return (
    <div className="space-y-10">
      {/* Header & Controls */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <div className="space-y-6">
          <h2 className="font-display text-4xl font-bold">Template Management</h2>
          <div className="flex items-center gap-2 p-1 bg-surface-container-low rounded-2xl border border-border-subtle w-fit overflow-x-auto no-scrollbar">
            {categories.map((cat) => {
              const isActive = (cat === 'All Templates' && !category) || cat === category;
              return (
                <button
                  key={cat}
                  onClick={() => setCategory(cat === 'All Templates' ? null : cat)}
                  className={cn(
                    'px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all',
                    isActive
                      ? 'bg-electric-violet text-white shadow-lg shadow-electric-violet/20'
                      : 'text-on-surface-variant hover:text-on-surface hover:bg-white/5',
                  )}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
        <button className="shimmer-btn flex items-center justify-center gap-2 px-8 py-4 rounded-2xl text-white font-display text-lg font-bold shadow-xl active:scale-95 transition-transform shrink-0">
          <PlusCircle className="w-6 h-6" />
          <span>Add New Template</span>
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Gallery Grid */}
      <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
        {filtered.map((template) => {
          const isToggling = togglingId === template.id;

          return (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                'break-inside-avoid glass-panel rounded-2xl overflow-hidden group transition-all relative',
                !template.isActive && 'opacity-85 grayscale-[0.25] border-red-500/30',
              )}
            >
              <div className="relative overflow-hidden">
                <img
                  src={template.previewImageUrl}
                  alt={template.name}
                  className="w-full h-auto object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 flex flex-col gap-1 items-start">
                  {template.isPremium && (
                    <span className="px-3 py-1 bg-tertiary-container/90 backdrop-blur-md rounded-full text-white text-[10px] font-bold uppercase tracking-widest">
                      Premium
                    </span>
                  )}
                  <span
                    className={cn(
                      'px-3 py-1 backdrop-blur-md rounded-full text-white text-[10px] font-bold uppercase tracking-widest',
                      template.isActive ? 'bg-emerald-500/90' : 'bg-red-500/90',
                    )}
                  >
                    {template.isActive ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-electric-violet uppercase tracking-widest leading-none">
                    {template.occasionType}
                  </span>
                  {/* Status Toggle Switch */}
                  <button
                    onClick={() => handleToggleActive(template)}
                    disabled={isToggling}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all border',
                      template.isActive
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                        : 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20',
                    )}
                    title={template.isActive ? 'Click to Disable Template' : 'Click to Activate Template'}
                  >
                    <Power className="w-3 h-3" />
                    <span>{template.isActive ? 'Active' : 'Disabled'}</span>
                  </button>
                </div>

                <h4 className="font-display text-lg font-bold text-on-surface leading-snug">
                  {template.name}
                </h4>
                {template.description && (
                  <p className="text-xs text-on-surface-variant line-clamp-2">
                    {template.description}
                  </p>
                )}

                {/* Usage Counters & Site Revocation Trigger */}
                <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-2 text-on-surface-variant font-medium">
                    <span className="text-emerald-400 font-bold">
                      {template.publishedCount ?? 0} Published
                    </span>
                    <span>•</span>
                    <span>{template.draftCount ?? 0} Drafts</span>
                  </div>

                  {(template.publishedCount ?? 0) > 0 && (
                    <button
                      onClick={() => handleOpenSitesModal(template)}
                      className="text-amber-400 hover:text-amber-300 font-bold flex items-center gap-1 text-[10px] underline underline-offset-2"
                    >
                      <Eye className="w-3 h-3" />
                      <span>Inspect Sites</span>
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center text-on-surface-variant text-sm">No templates found.</div>
      )}

      {/* Published Sites Inspection & Controlled Revocation Modal */}
      <AnimatePresence>
        {selectedTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-container-high border border-white/10 rounded-3xl max-w-2xl w-full p-6 space-y-6 shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div>
                  <h3 className="font-display text-2xl font-bold flex items-center gap-2">
                    <span>Published Sites Impact</span>
                    <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-bold">
                      {selectedTemplate.name}
                    </span>
                  </h3>
                  <p className="text-xs text-on-surface-variant mt-1">
                    Template status: {selectedTemplate.isActive ? 'Active' : 'Disabled (New creates blocked, existing sites live)'}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="p-2 rounded-full hover:bg-white/10 text-on-surface-variant hover:text-on-surface"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {loadingSites ? (
                <div className="py-8 text-center text-on-surface-variant text-sm">
                  Loading live published sites...
                </div>
              ) : sitesError ? (
                <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-xl">
                  {sitesError}
                </div>
              ) : publishedSites.length === 0 ? (
                <div className="py-8 text-center text-on-surface-variant text-sm">
                  No active published sites using this template.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="max-h-60 overflow-y-auto space-y-2 pr-1 no-scrollbar">
                    {publishedSites.map((site) => (
                      <div
                        key={site.id}
                        className="p-3 bg-white/5 border border-white/5 rounded-xl flex items-center justify-between text-xs"
                      >
                        <div>
                          <p className="font-bold text-on-surface">
                            {site.eventName || 'Untitled Event'}
                          </p>
                          <p className="text-[10px] text-on-surface-variant">
                            Slug: {site.publicSlug || 'N/A'} • Published:{' '}
                            {site.publishedAt ? new Date(site.publishedAt).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        {site.hostedUrl && (
                          <a
                            href={site.hostedUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="p-2 rounded-lg bg-electric-violet/20 text-electric-violet hover:bg-electric-violet/30 transition-colors flex items-center gap-1 font-bold"
                          >
                            <span>Visit</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/30 space-y-3">
                    <div className="flex items-start gap-3">
                      <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-red-300 text-sm">
                          Revoke / Stop All Live Sites ({publishedSites.length})
                        </h4>
                        <p className="text-xs text-red-300/80 mt-1">
                          By default, deactivating a template leaves existing published sites live. If this template contains broken assets or policy violations, you can suspend all live sites using it.
                        </p>
                      </div>
                    </div>

                    <input
                      type="text"
                      placeholder="Reason for revocation (e.g. Deprecated template, asset copyright)"
                      value={revokeReason}
                      onChange={(e) => setRevokeReason(e.target.value)}
                      className="w-full px-3 py-2 bg-black/40 border border-red-500/30 rounded-xl text-xs text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-red-400"
                    />

                    <button
                      onClick={handleStopPublishedSites}
                      disabled={stoppingSites}
                      className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      <span>{stoppingSites ? 'Suspending Sites...' : 'Stop All Live Published Sites'}</span>
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Engagement Section */}
      <section className="py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 glass-panel rounded-3xl p-8 flex flex-col md:flex-row items-center gap-10">
          <div className="flex-1 space-y-6">
            <div>
              <h3 className="font-display text-3xl font-bold leading-none">Template Engagement</h3>
              <p className="text-on-surface-variant mt-4 text-base">
                Your templates have been viewed by over <span className="text-primary font-bold">45,000</span> unique visitors this month. The 'Luxury' category is currently trending.
              </p>
            </div>
            <div className="flex gap-4">
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5 flex-1">
                <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-widest mb-1">Conversion</p>
                <p className="text-2xl font-display font-bold text-green-400 leading-none">+12.5%</p>
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5 flex-1">
                <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-widest mb-1">Avg. Time</p>
                <p className="text-2xl font-display font-bold text-primary leading-none">4m 32s</p>
              </div>
            </div>
          </div>
          <div className="w-full md:w-64 aspect-square bg-gradient-to-br from-electric-violet/20 to-tertiary-container/20 rounded-2xl border border-white/10 flex items-center justify-center relative overflow-hidden group">
            <TrendingUp className="text-7xl text-electric-violet animate-pulse relative z-10" />
            <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
          </div>
        </div>

        <div className="lg:col-span-4 glass-panel rounded-3xl p-8 bg-gradient-to-br from-electric-violet/5 to-transparent flex flex-col">
          <h4 className="font-display text-xl font-bold leading-none mb-6">AI Insights</h4>
          <div className="space-y-6 flex-1">
            {[
              { label: 'Color Trend', text: 'Deep Emerald is gaining traction for Autumn invitations.', color: 'bg-electric-violet' },
              { label: 'Layout Tip', text: "Centered typography in 'Floral' cards increases click-through by 15%.", color: 'bg-tertiary-container' },
            ].map((insight) => (
              <div key={insight.label} className="flex gap-4 group">
                <div className={cn("w-1 rounded-full shrink-0 group-hover:translate-x-1 transition-transform", insight.color)} />
                <p className="text-sm text-on-surface-variant">
                  <strong className="text-on-surface font-bold tracking-tight">{insight.label}:</strong> {insight.text}
                </p>
              </div>
            ))}
          </div>
          <button className="w-full mt-8 py-3.5 border border-electric-violet/30 text-electric-violet text-xs font-bold uppercase tracking-widest rounded-xl hover:bg-electric-violet/10 transition-colors">
            Generate Full Report
          </button>
        </div>
      </section>
    </div>
  );
}

